import React, { useState, useEffect } from "react";

// ============================================
// AsegurAgua - Landing Page / Página Principal
// ============================================
// Instrucciones de integración:
// 1. Copia este archivo en: frontend/src/pages/Home.jsx
// 2. Agrega la ruta en App.js:
//    import Home from './pages/Home';
//    <Route path="/" element={<Home />} />
// 3. Asegúrate de que la ruta "/" apunte a Home
//    y "/login" al Login existente
// ============================================

const COLORS = {
  primary: "#0C4A6E",
  primaryLight: "#0369A1",
  accent: "#06B6D4",
  accentGlow: "#22D3EE",
  surface: "#F0F9FF",
  surfaceAlt: "#E0F2FE",
  text: "#0F172A",
  textSecondary: "#475569",
  white: "#FFFFFF",
  success: "#10B981",
  warning: "#F59E0B",
};

const WaterDrop = ({ delay = 0, left = "50%", size = 6 }) => (
  <div
    style={{
      position: "absolute",
      left,
      top: "-20px",
      width: size,
      height: size * 1.3,
      background: `linear-gradient(180deg, ${COLORS.accent}40, ${COLORS.accent}15)`,
      borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
      animation: `dropFall 3s ease-in infinite`,
      animationDelay: `${delay}s`,
      opacity: 0,
    }}
  />
);

const WavesSVG = () => (
  <svg
    viewBox="0 0 1440 120"
    style={{
      display: "block",
      width: "100%",
      height: "80px",
      marginTop: "-1px",
    }}
    preserveAspectRatio="none"
  >
    <defs>
      <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={COLORS.accent} stopOpacity="0.15" />
        <stop offset="50%" stopColor={COLORS.primaryLight} stopOpacity="0.1" />
        <stop offset="100%" stopColor={COLORS.accent} stopOpacity="0.15" />
      </linearGradient>
    </defs>
    <path
      d="M0,40 C360,100 720,0 1080,60 C1260,80 1380,40 1440,40 L1440,120 L0,120 Z"
      fill="url(#waveGrad)"
    >
      <animate
        attributeName="d"
        dur="8s"
        repeatCount="indefinite"
        values="
          M0,40 C360,100 720,0 1080,60 C1260,80 1380,40 1440,40 L1440,120 L0,120 Z;
          M0,60 C360,10 720,90 1080,30 C1260,10 1380,60 1440,50 L1440,120 L0,120 Z;
          M0,40 C360,100 720,0 1080,60 C1260,80 1380,40 1440,40 L1440,120 L0,120 Z
        "
      />
    </path>
    <path
      d="M0,60 C480,10 960,90 1440,50 L1440,120 L0,120 Z"
      fill={COLORS.primary}
      fillOpacity="0.06"
    >
      <animate
        attributeName="d"
        dur="6s"
        repeatCount="indefinite"
        values="
          M0,60 C480,10 960,90 1440,50 L1440,120 L0,120 Z;
          M0,40 C480,90 960,20 1440,70 L1440,120 L0,120 Z;
          M0,60 C480,10 960,90 1440,50 L1440,120 L0,120 Z
        "
      />
    </path>
  </svg>
);

// Iconos SVG inline
const Icons = {
  Water: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  ),
  Chart: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  Brain: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" />
      <line x1="10" y1="22" x2="14" y2="22" />
      <line x1="9" y1="9" x2="15" y2="9" />
      <line x1="12" y1="6" x2="12" y2="12" />
    </svg>
  ),
  Router: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="14" width="20" height="7" rx="2" />
      <path d="M12 3C8.5 3 5.4 4.6 3.4 7.1" />
      <path d="M20.6 7.1C18.6 4.6 15.5 3 12 3" />
      <path d="M8.5 10.5C9.4 9.6 10.6 9 12 9s2.6.6 3.5 1.5" />
      <circle cx="6" cy="17.5" r="1" />
      <circle cx="10" cy="17.5" r="1" />
    </svg>
  ),
  Shield: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  ),
  Users: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Eye: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Upload: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  ),
  ArrowRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Check: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
};

export default function Home() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const fadeIn = (delay = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
  });

  return (
    <div style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", color: COLORS.text, overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        
        * { box-sizing: border-box; margin: 0; padding: 0; }
        
        body { font-family: 'Plus Jakarta Sans', sans-serif; }
        
        @keyframes dropFall {
          0% { opacity: 0; transform: translateY(0); }
          10% { opacity: 0.6; }
          90% { opacity: 0.2; }
          100% { opacity: 0; transform: translateY(120px); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 32px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        
        .cta-primary {
          background: linear-gradient(135deg, ${COLORS.primaryLight}, ${COLORS.accent});
          color: white;
          box-shadow: 0 4px 15px ${COLORS.accent}40;
        }
        
        .cta-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px ${COLORS.accent}50;
        }
        
        .cta-secondary {
          background: white;
          color: ${COLORS.primary};
          border: 2px solid ${COLORS.primary}20;
        }
        
        .cta-secondary:hover {
          background: ${COLORS.surface};
          border-color: ${COLORS.accent};
          transform: translateY(-2px);
        }
        
        .app-card {
          background: white;
          border-radius: 20px;
          padding: 36px 28px;
          text-align: center;
          transition: all 0.4s ease;
          border: 1px solid #E2E8F0;
          position: relative;
          overflow: hidden;
        }
        
        .app-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, ${COLORS.primaryLight}, ${COLORS.accent});
          transform: scaleX(0);
          transition: transform 0.4s ease;
        }
        
        .app-card:hover::before {
          transform: scaleX(1);
        }
        
        .app-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px rgba(12, 74, 110, 0.1);
        }
        
        .process-step {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px 24px;
          background: white;
          border-radius: 16px;
          border: 1px solid #E2E8F0;
          transition: all 0.3s ease;
        }
        
        .process-step:hover {
          border-color: ${COLORS.accent};
          box-shadow: 0 4px 12px ${COLORS.accent}15;
        }
        
        .user-card {
          background: white;
          border-radius: 20px;
          padding: 32px;
          border: 1px solid #E2E8F0;
          transition: all 0.3s ease;
        }
        
        .user-card:hover {
          box-shadow: 0 12px 30px rgba(12, 74, 110, 0.08);
        }
        
        .section-label {
          display: inline-block;
          padding: 6px 16px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 12px;
        }
        
        @media (max-width: 768px) {
          .hero-grid { flex-direction: column !important; text-align: center !important; }
          .apps-grid { grid-template-columns: 1fr !important; }
          .users-grid { grid-template-columns: 1fr !important; }
          .process-grid { grid-template-columns: 1fr !important; }
          .hero-buttons { justify-content: center !important; }
          .footer-grid { grid-template-columns: 1fr !important; text-align: center !important; }
        }
      `}</style>

      {/* ======================== HERO ======================== */}
      <section
        style={{
          position: "relative",
          background: `linear-gradient(160deg, ${COLORS.white} 0%, ${COLORS.surface} 50%, ${COLORS.surfaceAlt} 100%)`,
          paddingTop: 40,
          minHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Gotas decorativas */}
        <WaterDrop delay={0} left="10%" size={8} />
        <WaterDrop delay={1.2} left="25%" size={5} />
        <WaterDrop delay={0.6} left="75%" size={7} />
        <WaterDrop delay={1.8} left="90%" size={6} />

        {/* Navbar */}
        <nav
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 40px",
            maxWidth: 1200,
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${COLORS.primaryLight}, ${COLORS.accent})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <Icons.Water />
            </div>
            <span
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: COLORS.primary,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              AsegurAgua
            </span>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <a href="/login" className="cta-secondary cta-btn" style={{ padding: "10px 24px", fontSize: 14 }}>
              Iniciar Sesión
            </a>
            <a href="/register" className="cta-primary cta-btn" style={{ padding: "10px 24px", fontSize: 14 }}>
              Registrarse
            </a>
          </div>
        </nav>

        {/* Hero Content */}
        <div
          className="hero-grid"
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 40px",
            maxWidth: 1200,
            margin: "0 auto",
            width: "100%",
            gap: 60,
          }}
        >
          <div style={{ flex: 1, ...fadeIn(0.1) }}>
            <div
              className="section-label"
              style={{
                background: `${COLORS.accent}15`,
                color: COLORS.primaryLight,
              }}
            >
              🇨🇴 Plataforma para Colombia
            </div>
            <h1
              style={{
                fontSize: "clamp(32px, 5vw, 52px)",
                fontWeight: 800,
                lineHeight: 1.15,
                color: COLORS.primary,
                marginBottom: 20,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              Gestión inteligente del{" "}
              <span
                style={{
                  background: `linear-gradient(135deg, ${COLORS.primaryLight}, ${COLORS.accent})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                agua potable
              </span>
            </h1>
            <p
              style={{
                fontSize: 18,
                lineHeight: 1.7,
                color: COLORS.textSecondary,
                maxWidth: 520,
                marginBottom: 32,
              }}
            >
              Monitorea y optimiza los procesos de tratamiento de agua en plantas
              municipales. Consulta datos de coagulación, floculación,
              sedimentación, filtración y desinfección con recomendaciones de
              nuestro mentor inteligente.
            </p>
            <div className="hero-buttons" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <a href="/register" className="cta-btn cta-primary">
                Comenzar ahora <Icons.ArrowRight />
              </a>
              <a href="/login" className="cta-btn cta-secondary">
                Ya tengo cuenta
              </a>
            </div>
          </div>

          {/* Visual - Proceso del agua */}
          <div
            style={{
              flex: 1,
              maxWidth: 480,
              ...fadeIn(0.3),
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: 24,
                padding: 32,
                boxShadow: `0 20px 60px ${COLORS.primary}12`,
                border: `1px solid ${COLORS.primary}10`,
                animation: "float 6s ease-in-out infinite",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.textSecondary,
                  marginBottom: 20,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Procesos monitoreados
              </div>
              {[
                { name: "Coagulación", pct: 95, color: "#0EA5E9" },
                { name: "Floculación", pct: 88, color: "#06B6D4" },
                { name: "Sedimentación", pct: 92, color: "#14B8A6" },
                { name: "Filtración", pct: 97, color: "#10B981" },
                { name: "Desinfección", pct: 99, color: "#059669" },
              ].map((item, i) => (
                <div key={i} style={{ marginBottom: i < 4 ? 16 : 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 14,
                      fontWeight: 500,
                      marginBottom: 6,
                      color: COLORS.text,
                    }}
                  >
                    <span>{item.name}</span>
                    <span style={{ color: item.color, fontWeight: 700 }}>{item.pct}%</span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      background: "#F1F5F9",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: visible ? `${item.pct}%` : "0%",
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${item.color}CC, ${item.color})`,
                        transition: `width 1.2s ease ${0.5 + i * 0.15}s`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <WavesSVG />
      </section>

      {/* ======================== APLICATIVOS ======================== */}
      <section style={{ padding: "80px 40px", background: COLORS.white }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56, ...fadeIn(0.1) }}>
            <div
              className="section-label"
              style={{ background: `${COLORS.accent}10`, color: COLORS.primaryLight }}
            >
              Tres módulos integrados
            </div>
            <h2
              style={{
                fontSize: "clamp(26px, 4vw, 38px)",
                fontWeight: 800,
                color: COLORS.primary,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              ¿Qué encontrarás en AsegurAgua?
            </h2>
          </div>

          <div
            className="apps-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 28,
            }}
          >
            {[
              {
                icon: <Icons.Chart />,
                title: "Operador del Agua Potable",
                desc: "Registra y consulta datos de los procesos de tratamiento: coagulación, floculación, sedimentación, filtración y desinfección. Visualiza gráficas en tiempo real.",
                color: COLORS.primaryLight,
              },
              {
                icon: <Icons.Brain />,
                title: "Mentor del Agua Potable",
                desc: "Recibe recomendaciones inteligentes para optimizar cada etapa del tratamiento. Análisis automático de parámetros y sugerencias de mejora.",
                color: COLORS.accent,
              },
              {
                icon: <Icons.Router />,
                title: "Enrutador al Agua Inteligente",
                desc: "Automatización de procesos de la planta de tratamiento. Conexión inteligente entre sensores, datos y acciones para una gestión eficiente.",
                color: COLORS.success,
              },
            ].map((app, i) => (
              <div key={i} className="app-card">
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background: `${app.color}12`,
                    color: app.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  {app.icon}
                </div>
                <h3
                  style={{
                    fontSize: 19,
                    fontWeight: 700,
                    color: COLORS.primary,
                    marginBottom: 12,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {app.title}
                </h3>
                <p style={{ fontSize: 15, lineHeight: 1.65, color: COLORS.textSecondary }}>
                  {app.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== PROCESOS ======================== */}
      <section
        style={{
          padding: "80px 40px",
          background: `linear-gradient(180deg, ${COLORS.surface}, ${COLORS.white})`,
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div
              className="section-label"
              style={{ background: `${COLORS.success}12`, color: COLORS.success }}
            >
              Etapas del tratamiento
            </div>
            <h2
              style={{
                fontSize: "clamp(26px, 4vw, 38px)",
                fontWeight: 800,
                color: COLORS.primary,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              Procesos que puedes monitorear
            </h2>
          </div>

          <div
            className="process-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              maxWidth: 900,
              margin: "0 auto",
            }}
          >
            {[
              { num: "01", name: "Coagulación", desc: "Desestabilización de partículas suspendidas" },
              { num: "02", name: "Floculación", desc: "Agrupación de partículas en flóculos" },
              { num: "03", name: "Sedimentación", desc: "Separación de sólidos por gravedad" },
              { num: "04", name: "Filtración", desc: "Eliminación de partículas finas" },
              { num: "05", name: "Desinfección", desc: "Eliminación de microorganismos" },
              { num: "✓", name: "Agua Potable", desc: "Agua segura para consumo humano" },
            ].map((step, i) => (
              <div key={i} className="process-step">
                <div
                  style={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: 12,
                    background:
                      i === 5
                        ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                        : `linear-gradient(135deg, ${COLORS.primaryLight}15, ${COLORS.accent}15)`,
                    color: i === 5 ? "white" : COLORS.primaryLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 15,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {step.num}
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: COLORS.primary,
                      marginBottom: 2,
                    }}
                  >
                    {step.name}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== USUARIOS ======================== */}
      <section style={{ padding: "80px 40px", background: COLORS.white }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div
              className="section-label"
              style={{ background: `${COLORS.primaryLight}12`, color: COLORS.primaryLight }}
            >
              Roles del sistema
            </div>
            <h2
              style={{
                fontSize: "clamp(26px, 4vw, 38px)",
                fontWeight: 800,
                color: COLORS.primary,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              ¿Quién puede usar AsegurAgua?
            </h2>
          </div>

          <div
            className="users-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 28,
            }}
          >
            {/* Admin */}
            <div className="user-card">
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: `${COLORS.primaryLight}12`,
                  color: COLORS.primaryLight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <Icons.Upload />
              </div>
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: COLORS.primary,
                  marginBottom: 8,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                Administrador del Agua
              </h3>
              <p
                style={{
                  fontSize: 15,
                  color: COLORS.textSecondary,
                  lineHeight: 1.6,
                  marginBottom: 20,
                }}
              >
                Gestiona y sube los datos de la planta de tratamiento municipal.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "Sube datos de procesos de tratamiento",
                  "Genera y visualiza gráficas",
                  "Recibe recomendaciones del mentor IA",
                  "Administra usuarios del sistema",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Icons.Check />
                    <span style={{ fontSize: 14, color: COLORS.text }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ciudadano */}
            <div className="user-card">
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: `${COLORS.accent}12`,
                  color: COLORS.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <Icons.Eye />
              </div>
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: COLORS.primary,
                  marginBottom: 8,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                Ciudadano
              </h3>
              <p
                style={{
                  fontSize: 15,
                  color: COLORS.textSecondary,
                  lineHeight: 1.6,
                  marginBottom: 20,
                }}
              >
                Consulta la calidad del agua potable de su municipio de forma transparente.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "Visualiza datos por medio de gráficas",
                  "Consulta estado del agua en tiempo real",
                  "Accede a informes de calidad",
                  "Conoce los procesos de tratamiento",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Icons.Check />
                    <span style={{ fontSize: 14, color: COLORS.text }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================== CTA FINAL ======================== */}
      <section
        style={{
          padding: "80px 40px",
          background: `linear-gradient(160deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 50%, ${COLORS.accent}20, transparent 50%),
                         radial-gradient(circle at 80% 50%, ${COLORS.accent}15, transparent 50%)`,
          }}
        />
        <div style={{ position: "relative", maxWidth: 600, margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "clamp(26px, 4vw, 36px)",
              fontWeight: 800,
              color: "white",
              marginBottom: 16,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            Comienza a monitorear la calidad del agua
          </h2>
          <p style={{ fontSize: 17, color: "rgba(255,255,255,0.8)", marginBottom: 36, lineHeight: 1.6 }}>
            Regístrate gratis y accede a los datos de tratamiento de agua potable de tu municipio.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <a
              href="/register"
              className="cta-btn"
              style={{
                background: "white",
                color: COLORS.primary,
                fontWeight: 700,
                padding: "16px 36px",
                fontSize: 16,
                borderRadius: 12,
                textDecoration: "none",
                transition: "all 0.3s ease",
              }}
            >
              Crear cuenta gratis <Icons.ArrowRight />
            </a>
            <a
              href="/login"
              className="cta-btn"
              style={{
                background: "transparent",
                color: "white",
                fontWeight: 600,
                padding: "16px 36px",
                fontSize: 16,
                borderRadius: 12,
                border: "2px solid rgba(255,255,255,0.3)",
                textDecoration: "none",
                transition: "all 0.3s ease",
              }}
            >
              Iniciar sesión
            </a>
          </div>
        </div>
      </section>

      {/* ======================== FOOTER ======================== */}
      <footer
        style={{
          padding: "40px",
          background: "#0B3D5C",
          color: "rgba(255,255,255,0.6)",
          fontSize: 14,
        }}
      >
        <div
          className="footer-grid"
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            gap: 40,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: `linear-gradient(135deg, ${COLORS.primaryLight}, ${COLORS.accent})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                </svg>
              </div>
              <span style={{ fontWeight: 700, color: "white", fontSize: 16 }}>AsegurAgua</span>
            </div>
            <p style={{ lineHeight: 1.6, maxWidth: 320 }}>
              Gestión inteligente de datos de plantas de tratamiento municipal en Colombia.
            </p>
          </div>
          <div>
            <div style={{ fontWeight: 600, color: "white", marginBottom: 12 }}>Plataforma</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <a href="/login" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>
                Iniciar Sesión
              </a>
              <a href="/register" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>
                Registrarse
              </a>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, color: "white", marginBottom: 12 }}>Módulos</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <span>Operador del Agua</span>
              <span>Mentor Inteligente</span>
              <span>Enrutador Inteligente</span>
            </div>
          </div>
        </div>
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            marginTop: 32,
            paddingTop: 24,
            textAlign: "center",
            maxWidth: 1100,
            margin: "32px auto 0",
          }}
        >
          © 2026 AsegurAgua — Territorio Conectado. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
}
